// Customer Experience Default Configuration Constants
// This file contains all default values for auto-generated customer experience configs
import { toDateString } from "../../utils/dateString";

// Default UI Styles
export const DEFAULT_UI_STYLES = {
  button: {
    width: 100,
    height: 50,
    widthType: "PERCENT",
    heightType: "FIXED",
    borderRadius: 10,
    borderWidth: 2,
    fontSize: 18,
    fontWeight: 100,
    fontStyle: "normal",
    fontColor: "#FFF",
    backgroundColor: "#000",
    borderColor: "#000",
  },
  message: {
    fontSize: 14,
    fontWeight: 400,
    fontColor: "#000",
    fontStyle: "normal",
    textAlign: "center",
  },
  badge: {
    fontSize: 14,
    fontWeight: 400,
    fontColor: "#ffffff",
    backgroundColor: "#000000",
  },
};

// Shipping Date Types
export const SHIPPING_DATE_TYPES = {
  ASAP: "ASAP",
  EXACT_DATE: "EXACT_DATE",
  PERIOD: "PERIOD",
  INTERVAL: "INTERVAL",
};

// Billing Policy Types
export const BILLING_POLICY_TYPES = {
  FULL_AMOUNT: "FULL_AMOUNT",
  PARTIAL_PAYMENT: "PARTIAL_PAYMENT",
};

// Remaining Payment Types
export const REMAINING_PAYMENT_TYPES = {
  EXACT_DATE: "EXACT_DATE",
  TIME_AFTER_CHECKOUT: "TIME_AFTER_CHECKOUT",
  ON_FULFILLMENT: "ON_FULFILLMENT",
};

// Pricing Policy Types
export const PRICING_POLICY_TYPES = {
  NO_DISCOUNT: "NO_DISCOUNT",
  PERCENTAGE_DISCOUNT: "PERCENTAGE_DISCOUNT",
  FIXED_AMOUNT_DISCOUNT: "FIXED_AMOUNT_DISCOUNT",
};

// Stock Options
export const STOCK_OPTIONS = {
  WHEN_OUT_OF_STOCK: "WHEN_OUT_OF_STOCK",
  WHEN_SHOPIFY_INVENTORY_AVAILABLE: "WHEN_SHOPIFY_INVENTORY_AVAILABLE",
};

// Inventory Policies
export const INVENTORY_POLICIES = {
  ON_SALE: "ON_SALE",
};

// Timer Configuration Types
export const TIMER_DATE_TYPES = {
  CURRENT: "CURRENT", // Countdown to a date
  FIXED: "FIXED", // Fixed minutes
};

export const TIMER_START_AT = {
  NOW: "NOW", // Timer starts right now
  LATER: "LATER", // Timer starts at a specific time
};

export const TIMER_END_AT = {
  DATE: "DATE", // Specific date
};

export const ONCE_IT_ENDS_OPTIONS = {
  UNPUBLISH_TIMER: "unpublish-timer",
  REPEAT_COUNTDOWN: "repeat-the-countdown",
  SHOW_TITLE: "show-custom-title",
  DO_NOTHING: "do-nothing",
};

// Default Timer Configuration
export const DEFAULT_TIMER_CONFIG = {
  isEnabled: false,
  content: {
    title: "Hurry up!",
    subheading: "Sale ends in:",
    labels: {
      days: "Days",
      hours: "Hrs",
      mins: "Mins",
      secs: "Secs",
    },
    dates: {
      type: TIMER_DATE_TYPES.CURRENT, // Default to countdown to a date
      startAt: TIMER_START_AT.NOW, // Timer starts right now
      startDate: toDateString(new Date()),
      endAt: TIMER_END_AT.DATE, // Specific date
      endDate: toDateString(new Date(new Date().getTime() + 24 * 60 * 60 * 1000)), // 24 hours from now
      minutes: 1440, // 24 hours in minutes (for fixed timer fallback)
    },
    onceItEnds: {
      value: ONCE_IT_ENDS_OPTIONS.UNPUBLISH_TIMER,
      customTitle: "",
    },
  },
  style: {
    container: {
      background: "#2c2e4a",
      borderRadius: 8,
      borderSize: 0,
      borderColor: "#FFF",
      paddingTop: 30,
      paddingBottom: 30,
      marginTop: 20,
      marginBottom: 20,
    },
    title: {
      fontSize: 30,
      color: "#c4c6e3",
    },
    subheading: {
      fontSize: 18,
      color: "#bdc3fb",
    },
    timer: {
      fontSize: 44,
      color: "#E84545",
    },
    labels: {
      fontSize: 14,
      color: "#bdc3fb",
    },
  },
  placement: "BELOW",
};

// Default Selling Plan Widget Configuration (for partial payment display on product page)
// Note: This widget is only shown when billingPolicyType is PARTIAL_PAYMENT
export const DEFAULT_SELLING_PLAN_WIDGET_CONFIG = {
  content: {
    // Description text shown in the widget
    description: "Pay a deposit now, the rest when it ships.",
  },
  style: {
    // Container styles
    maxWidth: 400,
    borderWidth: 1.5,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    padding: 14,
    // Description font styles
    fontSize: 13,
    fontColor: "#6b7280",
    fontWeight: 400,
    // Radio button styles
    radioSize: 20,
    radioSelectedColor: "#000000",
  },
};

// Default Cart Configuration
export const DEFAULT_CART_CONFIG = {
  // Cart line item label
  label: "Pre-order item delivers as soon as available.",

  // Pre-order alert
  alert: {
    isEnabled: false,
    triggers: {
      onCartPage: true,
      onButtonClick: false,
    },
    content: {
      title: "Important Notice",
      message:
        "By placing this order you acknowledge that you are informed about and agree to our shipping policy, our terms of service and our refund policy.",
      buttonText: "I agree",
      dismissLabel: "Don't show this message again.",
    },
    style: {
      buttonBackgroundColor: "#f70427",
      buttonTextColor: "#FFFFFF",
    },
  },

  // Mixed cart alert (pre-order + regular items)
  mixedAlert: {
    isEnabled: false,
    triggers: {
      onCartPage: true,
      onButtonClick: false,
    },
    content: {
      title: "Important Notice",
      message:
        "Because your cart contains both in-stock products and out-of-stock products your order will be shipped when out-of-stock products are restocked.",
      buttonText: "Okay, Got it!",
      dismissLabel: "Don't show this message again.",
    },
    style: {
      buttonBackgroundColor: "#f70427",
      buttonTextColor: "#FFFFFF",
    },
  },
};

// Default Notify Me Form Configuration
export const DEFAULT_NOTIFY_ME_FORM_CONFIG = {
  content: {
    title: "Notify me when available",
    description: "Subscribe to get notified when this product is back in stock.",
    emailPlaceholder: "Your email (e.g. name@example.com)",
    buttonText: "Notify Me",
    note: "We'll send you an email when this item is available.",
    successMessage: "Thank you! We'll notify you when this product is available.",
    successButtonText: "Got it",
    errorMessage: "Something went wrong. Please try again.",
  },
  newsletter: {
    isEnabled: false,
    label: "Subscribe to our newsletter for updates and exclusive offers.",
  },
  style: {
    fontSize: 16,
    borderRadius: 8,
    fieldsBorderRadius: 4,
    headingColor: "#000000",
    textColor: "#333333",
    buttonTextColor: "#FFFFFF",
    buttonBackgroundColor: "#000000",
    useGradient: false,
  },
  showBranding: true,
};

// Default Pre-Order Configuration
export const DEFAULT_PRE_ORDER_CONFIG = {
  category: "PREORDER",
  stockOption: STOCK_OPTIONS.WHEN_OUT_OF_STOCK,
  shippingDateType: SHIPPING_DATE_TYPES.ASAP,
  shippingDateJsonb: {
    specificDate: toDateString(new Date()),
    periodValue: 1,
    periodType: "DAY",
    periodStart: { value: 1, type: "DAY" },
    periodEnd: { value: 7, type: "DAY" },
  },
  billingPolicyType: BILLING_POLICY_TYPES.FULL_AMOUNT,
  billingPolicyJsonb: {
    upfrontPayment: { type: "PERCENTAGE", value: 10 },
    remainingPaymentType: REMAINING_PAYMENT_TYPES.EXACT_DATE,
    remainingPaymentDate: toDateString(new Date()),
    timeAfterCheckout: { value: 1, unit: "DAY" },
  },
  inventoryPolicy: INVENTORY_POLICIES.ON_SALE,
  autoEnableContinueSelling: true,
  pricingPolicyType: PRICING_POLICY_TYPES.NO_DISCOUNT,
  pricingPolicyValue: 0,
  uiConfig: {
    button: {
      type: "DEFAULT",
      text: "Pre-Order",
      addToCartText: "Add to Cart",
      soldOutText: "Sold Out",
      style: { ...DEFAULT_UI_STYLES.button },
    },
    message: {
      text: "Pre-order delivers as soon as available.",
      placementType: "BELOW",
      style: { ...DEFAULT_UI_STYLES.message },
    },
    badge: {
      isEnabled: false,
      text: "Pre-Order",
      type: "ROUNDED",
      top: true,
      right: true,
      style: { ...DEFAULT_UI_STYLES.badge },
    },
    timer: { ...DEFAULT_TIMER_CONFIG },
    cart: { ...DEFAULT_CART_CONFIG },
    sellingPlanWidget: { ...DEFAULT_SELLING_PLAN_WIDGET_CONFIG },
    collection: {
      button: {
        type: "DEFAULT",
        text: "Pre-Order",
        addToCartText: "Add to Cart",
        soldOutText: "Sold Out",
        style: { ...DEFAULT_UI_STYLES.button },
      },
      message: {
        text: "Pre-order delivers as soon as available.",
        placementType: "BELOW",
        style: { ...DEFAULT_UI_STYLES.message },
      },
      badge: {
        isEnabled: false,
        text: "Pre-Order",
        type: "ROUNDED",
        top: true,
        right: true,
        style: { ...DEFAULT_UI_STYLES.badge },
      },
    },
  },
};

// Default Back in Stock Configuration
export const DEFAULT_BACK_IN_STOCK_CONFIG = {
  category: "BACK_IN_STOCK",
  stockOption: STOCK_OPTIONS.WHEN_OUT_OF_STOCK,
  quantityThreshold: 1,
  uiConfig: {
    button: {
      type: "CUSTOM",
      text: "Notify when available",
      addToCartText: "Add to Cart",
      soldOutText: "Sold Out",
      style: { ...DEFAULT_UI_STYLES.button },
    },
    message: {
      text: "Subscribe to get notified when this product is back in stock.",
      placementType: "BELOW",
      style: { ...DEFAULT_UI_STYLES.message },
    },
    badge: {
      isEnabled: false,
      text: "Notify Me",
      type: "ROUNDED",
      top: true,
      right: true,
      style: { ...DEFAULT_UI_STYLES.badge },
    },
    timer: { ...DEFAULT_TIMER_CONFIG },
    notifyMeForm: { ...DEFAULT_NOTIFY_ME_FORM_CONFIG },
    collection: {
      button: {
        type: "CUSTOM",
        text: "Notify when available",
        addToCartText: "Add to Cart",
        soldOutText: "Sold Out",
        style: { ...DEFAULT_UI_STYLES.button },
      },
      message: {
        text: "Subscribe to get notified when this product is back in stock.",
        placementType: "BELOW",
        style: { ...DEFAULT_UI_STYLES.message },
      },
      badge: {
        isEnabled: false,
        text: "Notify Me",
        type: "ROUNDED",
        top: true,
        right: true,
        style: { ...DEFAULT_UI_STYLES.badge },
      },
      notifyMeForm: { ...DEFAULT_NOTIFY_ME_FORM_CONFIG },
    },
  },
};

// Default Coming Soon Configuration
export const DEFAULT_COMING_SOON_CONFIG = {
  category: "COMING_SOON",
  stockOption: STOCK_OPTIONS.WHEN_OUT_OF_STOCK,
  uiConfig: {
    button: {
      type: "CUSTOM",
      text: "Coming Soon",
      addToCartText: "Add to Cart",
      soldOutText: "Sold Out",
      style: { ...DEFAULT_UI_STYLES.button },
    },
    message: {
      text: "Product will be available soon.",
      placementType: "BELOW",
      style: { ...DEFAULT_UI_STYLES.message },
    },
    badge: {
      isEnabled: false,
      text: "Coming Soon",
      type: "ROUNDED",
      top: true,
      right: true,
      style: { ...DEFAULT_UI_STYLES.badge },
    },
    timer: { ...DEFAULT_TIMER_CONFIG },
    collection: {
      button: {
        type: "CUSTOM",
        text: "Coming Soon",
        addToCartText: "Add to Cart",
        soldOutText: "Sold Out",
        style: { ...DEFAULT_UI_STYLES.button },
      },
      message: {
        text: "Product will be available soon.",
        placementType: "BELOW",
        style: { ...DEFAULT_UI_STYLES.message },
      },
      badge: {
        isEnabled: false,
        text: "Coming Soon",
        type: "ROUNDED",
        top: true,
        right: true,
        style: { ...DEFAULT_UI_STYLES.badge },
      },
    },
  },
};

// Default Low Stock Configuration
export const DEFAULT_LOW_STOCK_CONFIG = {
  category: "LOW_STOCK",
  stockOption: STOCK_OPTIONS.WHEN_SHOPIFY_INVENTORY_AVAILABLE,
  quantityThreshold: 3,
  uiConfig: {
    button: {
      type: "DEFAULT",
      text: "Low Stock",
      addToCartText: "Add to Cart",
      soldOutText: "Sold Out",
      style: { ...DEFAULT_UI_STYLES.button },
    },
    message: {
      text: "Low stock - order now before it sells out!",
      placementType: "BELOW",
      style: { ...DEFAULT_UI_STYLES.message },
    },
    badge: {
      isEnabled: false,
      text: "Low Stock",
      type: "ROUNDED",
      top: true,
      right: true,
      style: { ...DEFAULT_UI_STYLES.badge },
    },
    timer: { ...DEFAULT_TIMER_CONFIG },
    collection: {
      button: {
        type: "DEFAULT",
        text: "Low Stock",
        addToCartText: "Add to Cart",
        soldOutText: "Sold Out",
        style: { ...DEFAULT_UI_STYLES.button },
      },
      message: {
        text: "Low stock - order now before it sells out!",
        placementType: "BELOW",
        style: { ...DEFAULT_UI_STYLES.message },
      },
      badge: {
        isEnabled: false,
        text: "Low Stock",
        type: "ROUNDED",
        top: true,
        right: true,
        style: { ...DEFAULT_UI_STYLES.badge },
      },
    },
  },
};

// Default Arrays
export const DEFAULT_ARRAYS = {
  markets: [],
  inventoryLocations: [],
};

// Category constants
export const PRE_ORDER_CATEGORIES = {
  PREORDER: "PREORDER",
  BACKORDER: "BACKORDER",
  MADE_TO_ORDER: "MADE_TO_ORDER",
};

export const BACK_IN_STOCK_CATEGORIES = {
  BACK_IN_STOCK: "BACK_IN_STOCK",
  NOTIFY_ME: "NOTIFY_ME",
  WAITLIST: "WAITLIST",
};

export const COMING_SOON_CATEGORIES = {
  COMING_SOON: "COMING_SOON",
};

export const LOW_STOCK_CATEGORIES = {
  LOW_STOCK: "LOW_STOCK",
};

// Category UI texts -- only text/content fields, mirrors uiConfig shape for deep merge
export const CATEGORY_UI_TEXTS = {
  [PRE_ORDER_CATEGORIES.PREORDER]: {
    button: { text: "Pre-Order" },
    message: { text: "Pre-order delivers as soon as available." },
    badge: { text: "Pre-Order" },
    cart: { label: "Pre-order item delivers as soon as available." },
    collection: {
      button: { text: "Pre-Order" },
      message: { text: "Pre-order delivers as soon as available." },
      badge: { text: "Pre-Order" },
    },
  },
  [PRE_ORDER_CATEGORIES.BACKORDER]: {
    button: { text: "Back Order" },
    message: { text: "Back-order delivers when stock is replenished." },
    badge: { text: "Back Order" },
    cart: { label: "Back-order item delivers when stock is available." },
    collection: {
      button: { text: "Back Order" },
      message: { text: "Back-order delivers when stock is replenished." },
      badge: { text: "Back Order" },
    },
  },
  [PRE_ORDER_CATEGORIES.MADE_TO_ORDER]: {
    button: { text: "Made to Order" },
    message: { text: "This item is custom-made. Please allow extra production time." },
    badge: { text: "Made to Order" },
    cart: { label: "Made-to-order item — please allow extra production time." },
    collection: {
      button: { text: "Made to Order" },
      message: { text: "This item is custom-made. Please allow extra time." },
      badge: { text: "Made to Order" },
    },
  },
  [BACK_IN_STOCK_CATEGORIES.BACK_IN_STOCK]: {
    button: { text: "Notify when available" },
    message: { text: "Subscribe to get notified when this product is back in stock." },
    badge: { text: "Notify Me" },
    notifyMeForm: {
      content: {
        title: "Notify me when available",
        description: "Subscribe to get notified when this product is back in stock.",
        buttonText: "Notify Me",
        note: "We'll send you an email when this item is available.",
        successMessage: "Thank you! We'll notify you when this product is available.",
      },
    },
    collection: {
      button: { text: "Notify when available" },
      message: { text: "Subscribe to get notified when this product is back in stock." },
      badge: { text: "Notify Me" },
      notifyMeForm: {
        content: {
          title: "Notify me when available",
          description: "Subscribe to get notified when this product is back in stock.",
          buttonText: "Notify Me",
          note: "We'll send you an email when this item is available.",
          successMessage: "Thank you! We'll notify you when this product is available.",
        },
      },
    },
  },
  [BACK_IN_STOCK_CATEGORIES.NOTIFY_ME]: {
    button: { text: "Notify Me" },
    message: { text: "Get notified when this product is available again." },
    badge: { text: "Notify Me" },
    notifyMeForm: {
      content: {
        title: "Get notified",
        description: "Enter your email to be notified when this product is available.",
        buttonText: "Notify Me",
        note: "We'll email you as soon as it's back.",
        successMessage: "You're on the list! We'll let you know.",
      },
    },
    collection: {
      button: { text: "Notify Me" },
      message: { text: "Get notified when this product is available." },
      badge: { text: "Notify Me" },
      notifyMeForm: {
        content: {
          title: "Get notified",
          description: "Enter your email to be notified when this product is available.",
          buttonText: "Notify Me",
          note: "We'll email you as soon as it's back.",
          successMessage: "You're on the list! We'll let you know.",
        },
      },
    },
  },
  [BACK_IN_STOCK_CATEGORIES.WAITLIST]: {
    button: { text: "Join Waitlist" },
    message: { text: "Join the waitlist to be the first to know when it's back." },
    badge: { text: "Waitlist" },
    notifyMeForm: {
      content: {
        title: "Join the waitlist",
        description: "Be the first to know when this product is available again.",
        buttonText: "Join Waitlist",
        note: "We'll notify you by email when it's back in stock.",
        successMessage: "You're on the waitlist! We'll notify you when it's available.",
      },
    },
    collection: {
      button: { text: "Join Waitlist" },
      message: { text: "Join the waitlist for this product." },
      badge: { text: "Waitlist" },
      notifyMeForm: {
        content: {
          title: "Join the waitlist",
          description: "Be the first to know when this product is available again.",
          buttonText: "Join Waitlist",
          note: "We'll notify you by email when it's back in stock.",
          successMessage: "You're on the waitlist! We'll notify you when it's available.",
        },
      },
    },
  },
  [COMING_SOON_CATEGORIES.COMING_SOON]: {
    button: { text: "Coming Soon" },
    message: { text: "Product will be available soon." },
    badge: { text: "Coming Soon" },
    collection: {
      button: { text: "Coming Soon" },
      message: { text: "Product will be available soon." },
      badge: { text: "Coming Soon" },
    },
  },
  [LOW_STOCK_CATEGORIES.LOW_STOCK]: {
    button: { text: "Low Stock" },
    message: { text: "Low stock - order now before it sells out!" },
    badge: { text: "Low Stock" },
    collection: {
      button: { text: "Low Stock" },
      message: { text: "Low stock - order now before it sells out!" },
      badge: { text: "Low Stock" },
    },
  },
};

// Feature Configuration Map
export const FEATURE_CONFIG_MAP = {
  preOrder: {
    enabled: "preOrderEnabled",
    config: "preOrderConfig",
    defaultConfig: DEFAULT_PRE_ORDER_CONFIG,
  },
  backInStock: {
    enabled: "backInStockEnabled",
    config: "backInStockConfig",
    defaultConfig: DEFAULT_BACK_IN_STOCK_CONFIG,
  },
  comingSoon: {
    enabled: "comingSoonEnabled",
    config: "comingSoonConfig",
    defaultConfig: DEFAULT_COMING_SOON_CONFIG,
  },
  lowStock: {
    enabled: "lowStockEnabled",
    config: "lowStockConfig",
    defaultConfig: DEFAULT_LOW_STOCK_CONFIG,
  },
};

// Feature category groups -- maps each category to its feature key in FEATURE_CONFIG_MAP
export const FEATURE_CATEGORY_GROUPS = [
  {
    featureKey: "preOrder",
    categories: Object.values(PRE_ORDER_CATEGORIES),
  },
  {
    featureKey: "backInStock",
    categories: Object.values(BACK_IN_STOCK_CATEGORIES),
  },
  {
    featureKey: "comingSoon",
    categories: Object.values(COMING_SOON_CATEGORIES),
  },
  {
    featureKey: "lowStock",
    categories: Object.values(LOW_STOCK_CATEGORIES),
  },
];

// Market and Location Configuration Types
export const MARKET_CONFIG_TYPES = {
  GLOBAL: "GLOBAL",
  ISO_CODE: "ISO_CODE",
  SHOPIFY_MARKET: "SHOPIFY_MARKET",
};

export const LOCATION_CONFIG_TYPES = {
  ALL: "ALL",
  SPECIFIC: "SPECIFIC",
};

export const NOTIFICATION_CHANNEL_TYPES = {
  BUILT_IN: "BUILT_IN",
  SHOPIFY_FLOW: "SHOPIFY_FLOW",
  KLAVIYO: "KLAVIYO",
};
